import type { ComponentType } from 'react';
import Overview from './Overview';
import Safety from './Safety';
import FirstPlugin from './FirstPlugin';
import Protocol from './Protocol';
import Manifest from './Manifest';
import Publishing from './Publishing';

export interface DocSection {
  id: string;
  title: string;
}

export interface DocPage {
  slug: string;
  title: string;
  group: string;
  description: string;
  sections: DocSection[];
  component: ComponentType;
}

// Sidebar groups render in first-appearance order; prev/next follows array order.
export const DOC_PAGES: DocPage[] = [
  {
    slug: 'overview',
    title: 'Overview',
    group: 'Getting started',
    description:
      'Plugins are separate programs with their own permissions, signatures, and capability labels. The short version of how the whole system works.',
    sections: [
      { id: 'how-it-works', title: 'How it works' },
      { id: 'surface', title: 'What a plugin gets' },
      { id: 'risk-labels', title: 'Capability labels' },
      { id: 'sources', title: 'Where plugins come from' },
      { id: 'spec', title: 'The specification' },
    ],
    component: Overview,
  },
  {
    slug: 'safety',
    title: 'Install and safety',
    group: 'Getting started',
    description:
      'What is verified before any code runs, what you approve at install, and the controls you keep afterward.',
    sections: [
      { id: 'installing', title: 'What happens at install' },
      { id: 'consent', title: 'The consent dialog' },
      { id: 'credentials', title: 'Your Twitch login' },
      { id: 'community-sources', title: 'Community sources' },
      { id: 'controls', title: 'Your controls' },
    ],
    component: Safety,
  },
  {
    slug: 'first-plugin',
    title: 'Your first plugin',
    group: 'Build',
    description:
      'From an empty folder to a plugin running inside StreamNook, in any language that can read stdin and write stdout.',
    sections: [
      { id: 'layout', title: 'The folder' },
      { id: 'manifest', title: 'A minimal manifest' },
      { id: 'run', title: 'Run it inside StreamNook' },
      { id: 'implement', title: 'What you must implement' },
      { id: 'state', title: 'Logs and state' },
    ],
    component: FirstPlugin,
  },
  {
    slug: 'protocol',
    title: 'Protocol',
    group: 'Build',
    description:
      'JSON-RPC 2.0 over stdio: transport, lifecycle, events, host methods, and errors. Protocol version 1 is frozen.',
    sections: [
      { id: 'transport', title: 'Transport' },
      { id: 'lifecycle', title: 'Lifecycle' },
      { id: 'events', title: 'Events' },
      { id: 'host-methods', title: 'Host methods' },
      { id: 'errors', title: 'Errors' },
      { id: 'versioning', title: 'Versioning' },
    ],
    component: Protocol,
  },
  {
    slug: 'manifest',
    title: 'Manifest reference',
    group: 'Build',
    description:
      'Every field of plugin.toml, the permission contract, the capability tiers, and the validation order at install.',
    sections: [
      { id: 'top-level', title: 'Top-level fields' },
      { id: 'runtime', title: 'Runtime' },
      { id: 'capabilities', title: 'Capabilities' },
      { id: 'tiers', title: 'Capability tiers' },
      { id: 'validation', title: 'Validation at install' },
    ],
    component: Manifest,
  },
  {
    slug: 'publishing',
    title: 'Publishing',
    group: 'Publish',
    description:
      'Sign a release with minisign, host it on a GitHub release, and open a pull request to the official index.',
    sections: [
      { id: 'keys', title: 'Create your signing key' },
      { id: 'package', title: 'Package and sign' },
      { id: 'host', title: 'Host the release' },
      { id: 'index-entry', title: 'Open a pull request' },
      { id: 'review', title: 'What review checks' },
      { id: 'updates', title: 'Updating a release' },
      { id: 'policy', title: 'Index policy' },
    ],
    component: Publishing,
  },
];
